import AxiosInstance from '@/api/axios';
import BeaconAxiosInstance from '@/api/beaconAxios';
import '@/assets/css/themes/echo.css';
import '@/assets/css/vendors/simplebar.css';
import userFallback from '@/assets/images/users/icon-user.jpg';
import Breadcrumb from '@/components/Base/Breadcrumb';
import { Menu } from '@/components/Base/Headless';
import Lucide from '@/components/Base/Lucide';
import Tippy from '@/components/Base/Tippy';
import ChatSidebar from '@/components/Pulse/sidebar';
import {
  selectCompactMenu,
  setCompactMenu as setCompactMenuStore,
} from '@/stores/compactMenuSlice';
import { setBranchId, setHubspotCompanyIds, setOrganizationId } from '@/stores/generalFilterSlice';
import { useAppDispatch, useAppSelector } from '@/stores/hooks';
import { resetStore } from '@/stores/resetStore';
import { resetPages } from '@/stores/sessionLogSlice';
import {
  selectSettingsView,
  selectSideMenu,
  selectWorkspaceContext,
  setAllowReEngage,
  setCurrentAgent,
  setGoogleAnalytics,
  updateMenu,
  type Menu as SideMenuItem,
} from '@/stores/sideMenuSlice';
import {
  selectChatSidebar,
  requestAuthRefresh,
} from '@/stores/chatSidebarSlice';
import { toggleAgentWidget } from '@/stores/agentWidgetSlice';
import { RootState } from '@/stores/store';
import { getClientTeam } from '@/stores/userSlice';
import appImages from '@/utils/appimg';
import encryptDecrypt from '@/utils/encryptDecrypt';
import { ROLE } from '@/utils/enum';
import { camelToTitleCase, getClientId, getEncryptedClientId, removeAgentIframe } from '@/utils/helper';
import { LLM_API_PREFIX, lumenFetch } from '@/utils/lumenauth';
import clsx from 'clsx';
import { createRef, Suspense, useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Transition } from 'react-transition-group';
import SimpleBar from 'simplebar';
import { enter, FormattedMenu, leave, linkTo, nestedMenu } from './side-menu';
import {
  clearActiveClient,
  fetchClientById,
  setAgents,
  setGoogleAnalyticBool,
  updateActiveClientMeta,
} from '@/stores/activeClientSlice';
import { clearLastClientId, getLastClientId, getSessionClientId } from '@/utils/lastClient';
import ReleaseNotesModal from '@/pages/ReleaseNotes';
import SupportSelectionModal from '@/components/Support/SupportSelectionModal';
import UiFeedbackPrompt from '@/components/UiFeedbackPrompt';
import AgentRail from '@/components/AgentRail';
import { SidebarDropdownClient } from '@/components/AppDropdowns';
import { checkSuperOwner, currentRole } from '@/utils/checkAccess';
import { getActiveAgent } from '@/utils/agents';
import {
  useSyncWorkspaceContextFromUrl,
  isChatRootPath,
} from '@/hooks/useSyncWorkspaceContextFromUrl';
import { isNonClientRoute } from '@/utils/nonClientRoutes';
import Loading from '@/components/Loading/Loading';

interface PathType {
  path: string;
  name: string;
}

// Friendly labels for the leaf segments of slug-bearing routes the
// breadcrumb can't render usefully on its own. The breadcrumb defaults
// to the last URL segment + camelToTitleCase, which on `/beacon/
// implement/_JN3u08TAKlcuPV3OUzkA` produced "JN3u08 T A K Icu P V3 O
// Uzk A" in the page header — exactly the random-text bug the QA
// flagged. The lookup is keyed by a path *pattern* (with `:slug` as
// the placeholder) so we don't have to special-case each individual
// slug value.
const BREADCRUMB_PATTERN_LABELS: Array<{ pattern: RegExp; label: string }> = [
  { pattern: /^\/beacon\/implement\/[^/]+\/agency\/?$/, label: 'Agency' },
  {
    pattern: /^\/beacon\/implement\/[^/]+\/self-serve\/?$/,
    label: 'Self-serve',
  },
  { pattern: /^\/beacon\/implement\/[^/]+\/?$/, label: 'Implement' },
];

// Heuristic: looks like an opaque slug (long mixed-case/digit token) we
// shouldn't surface as a label. Same regex covers Beacon report slugs,
// MongoDB ObjectIds, base64-ish IDs.
const SLUG_LIKE = /^[a-f0-9]{24,}$|^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

function deriveBreadcrumbName(pathname: string): string {
  for (const { pattern, label } of BREADCRUMB_PATTERN_LABELS) {
    if (pattern.test(pathname)) return label;
  }
  const segments = pathname.replace(/^\//, '').replace(/\/$/, '').split('/');
  const last = segments[segments.length - 1] ?? '';
  // If the leaf is slug-like, walk up until we hit a real segment so we
  // never echo the raw slug into the page header.
  if (SLUG_LIKE.test(last)) {
    for (let i = segments.length - 2; i >= 0; i--) {
      const candidate = segments[i] ?? '';
      if (candidate && !SLUG_LIKE.test(candidate)) return candidate;
    }
    return '';
  }
  return last;
}

// Multi-segment breadcrumb trail for Beacon sub-pages so each level
// (Beacon › Implement › Send to web team) is its own clickable link.
// The trail replaces the in-page "← Back to ..." buttons that used to
// live in BeaconImplement / SelfServe / AgencyHandoff / RunProgress —
// breadcrumb segments are now the canonical back-navigation. Returns
// `null` for non-Beacon paths so the caller falls back to the default
// 2-level (client + leaf) breadcrumb.
function deriveBeaconTrail(pathname: string): Array<{ label: string; path: string }> | null {
  const implementMatch = pathname.match(
    /^\/beacon\/implement\/([^/]+)(?:\/(agency|self-serve))?\/?$/,
  );
  if (implementMatch) {
    const slug = implementMatch[1];
    const leaf = implementMatch[2];
    const trail = [
      { label: 'Beacon', path: '/beacon' },
      { label: 'Implement', path: `/beacon/implement/${slug}` },
    ];
    if (leaf === 'agency') {
      trail.push({ label: 'Send to web team', path: pathname });
    } else if (leaf === 'self-serve') {
      trail.push({ label: 'Walkthrough', path: pathname });
    }
    return trail;
  }
  if (/^\/beacon-live\/?$/.test(pathname)) {
    return [
      { label: 'Beacon', path: '/beacon' },
      { label: 'Run', path: pathname },
    ];
  }
  if (/^\/beacon\/?$/.test(pathname)) {
    return [{ label: 'Beacon', path: '/beacon' }];
  }
  return null;
}

function normalizeClientId(raw: string | null): string {
  if (!raw) return '';
  return raw.split(',')[0]?.trim() || '';
}

const SETTINGS_ONLY_TITLES = new Set(['Configurations', 'Team', 'Billing', 'Help & Resources']);

const ACCORDION_SECTIONS = new Set(['INSIGHTS', 'MANAGE', 'CONTROL CENTRE']);

/**
 * Collapse every section but the first.
 *
 * Derived from the menu rather than hard-coded, because a custom role's nav is
 * built from the screens it holds — its first section is whichever one it
 * happens to have, not always INSIGHTS.
 */
const collapsedByDefault = (
  menu: Array<SideMenuItem | string>
): Record<string, boolean> => {
  const state: Record<string, boolean> = {};
  let seenFirst = false;
  for (const entry of menu) {
    if (typeof entry !== 'string' || !entry.trim()) continue;
    if (!ACCORDION_SECTIONS.has(entry)) continue;
    state[entry] = seenFirst;
    seenFirst = true;
  }
  return state;
};

function filterMenuForSettingsView(
  menu: Array<SideMenuItem | string>,
  settingsView: boolean,
): Array<SideMenuItem | string> {
  const kept: Array<SideMenuItem | string> = [];
  let pendingHeader: string | null = null;

  for (const entry of menu) {
    if (typeof entry === 'string') {
      pendingHeader = entry;
      continue;
    }
    const isSettingsItem = SETTINGS_ONLY_TITLES.has(entry.title);
    if (isSettingsItem !== settingsView) continue;
    if (pendingHeader !== null) {
      kept.push(pendingHeader);
      pendingHeader = null;
    }
    kept.push(entry);
  }
  return kept;
}

function deriveRouteClientId(pathname: string): string {
  if (isNonClientRoute(pathname)) {
    return getEncryptedClientId() ?? '';
  }
  return pathname.split('/').filter(Boolean)[0] ?? '';
}

function deriveRouteChatId(pathname: string): string | undefined {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 3 && segments[1] === 'chat') {
    return segments[2];
  }
  return undefined;
}

function Main() {
  const dispatch = useAppDispatch();
  const compactMenu = useAppSelector(selectCompactMenu);
  const setCompactMenu = (val: boolean) => {
    localStorage.setItem('compactMenu', val.toString());
    dispatch(setCompactMenuStore(val));
  };
  const [activeMobileMenu, setActiveMobileMenu] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>(
    {},
  );
  // Sections the user has actually toggled. Anything untouched follows the
  // default below, so a menu that changes shape does not strand a section
  // open or shut from a previous role.
  const [touchedSections, setTouchedSections] = useState<Record<string, true>>(
    {},
  );
  const toggleSection = (title: string) => {
    setTouchedSections((prev) => ({ ...prev, [title]: true }));
    setCollapsedSections((prev) => ({ ...prev, [title]: !prev[title] }));
  };
  const location = useLocation();
  const navigate = useNavigate();
  const [formattedMenu, setFormattedMenu] = useState<Array<FormattedMenu | string>>([]);
  const sideMenuStore = useAppSelector(selectSideMenu);
  const settingsView = useAppSelector(selectSettingsView);
  const sideMenu = () =>
    nestedMenu(filterMenuForSettingsView(sideMenuStore, settingsView), location);
  const scrollableRef = createRef<HTMLDivElement>();
  const [imgUrl, setImgUrl] = useState<string | null>(null);
  const [openReleaseNotes, setOpenReleaseNotes] = useState(false);
  const [openSupport, setOpenSupport] = useState(false);
  const [demoResetting, setDemoResetting] = useState(false);
  const isSuperOwner = checkSuperOwner();
  const isBeaconPage = location.pathname.includes('beacon');
  const runDemoReset = async () => {
    if (
      !window.confirm(
        'Demo reset? Wipes every Beacon run, monitor, implementation record, artefact and cache on the local backend, and clears the operator URL.',
      )
    ) {
      return;
    }
    setDemoResetting(true);
    try {
      await BeaconAxiosInstance.post('/beacon/_debug/reset-state?nuke=true');
      localStorage.setItem('demo:siteUrl', '');
      localStorage.setItem('demo:brandName', '');
      window.location.assign('/beacon');
    } finally {
      setDemoResetting(false);
    }
  };

  const pageDetails = useAppSelector((state) => state.sessionLog.pageDetails);

  const activeClient = useAppSelector((state: RootState) => state.activeClient);
  const clientName = activeClient.name;
  const client = activeClient.id;
  const clientProfilePic = activeClient.profile;
  const role = currentRole();

  const chatSidebar = useAppSelector(selectChatSidebar);
  const pathname = location.pathname;

  const routeClientId = deriveRouteClientId(pathname);
  const routeChatId = deriveRouteChatId(pathname);

  useSyncWorkspaceContextFromUrl();
  const workspaceContext = useAppSelector(selectWorkspaceContext);
  const showChatSidebar = workspaceContext.kind === 'intelligence';

  const isChatPage = isChatRootPath(pathname);

  const pulseClientId = normalizeClientId(getClientId());
  const chatFetchLlm = useCallback(
    (path: string, init?: RequestInit) =>
      lumenFetch(pulseClientId, `${LLM_API_PREFIX}${path}`, init),
    [pulseClientId]
  );

  const handleChatAuthFailure = useCallback(() => {
    dispatch(requestAuthRefresh());
  }, [dispatch]);

  const handleChatNewChat = useCallback(() => {
    if (routeClientId) {
      navigate(`/${routeClientId}`);
    }
    setActiveMobileMenu(false);
  }, [navigate, routeClientId]);

  const handleChatSelect = useCallback(
    (chatId: string) => {
      if (routeClientId) {
        navigate(`/${routeClientId}/chat/${chatId}`);
      }
      if (window.matchMedia('(max-width: 768px)').matches) {
        setActiveMobileMenu(false);
      }
    },
    [navigate, routeClientId]
  );

  useEffect(() => {
    if (!client) {
      const sessionClientId = getSessionClientId();
      const lastClientId = sessionClientId ?? getLastClientId();
      if (lastClientId) {
        dispatch(fetchClientById(lastClientId)).then((result) => {
          const encryptedId = encryptDecrypt.encryptClientId(lastClientId);
          dispatch(updateMenu(encryptedId));
          const reEngage = result.payload?.reEngage === true;
          dispatch(setAllowReEngage(reEngage));
        });
      } else {
        dispatch(updateMenu(null));
      }
    }
    const currentAgent = getActiveAgent();
    if (currentAgent) {
      dispatch(setCurrentAgent(currentAgent));
    }
  }, []);

  useEffect(() => {
    if (!client) {
      const sessionClientId = getSessionClientId();
      const lastClientId = sessionClientId ?? getLastClientId();
      if (lastClientId) {
        dispatch(fetchClientById(lastClientId)).then((result) => {
          const encryptedId = encryptDecrypt.encryptClientId(lastClientId);
          dispatch(updateMenu(encryptedId));
          const reEngage = result.payload?.reEngage === true;
          dispatch(setAllowReEngage(reEngage));
        });
      } else {
        dispatch(updateMenu(null));
      }
    }
    const currentAgent = getActiveAgent();
    if (currentAgent) {
      dispatch(setCurrentAgent(currentAgent));
    }
  }, []);

  useEffect(() => {
    if (!client) {
      const sessionClientId = getSessionClientId();
      const lastClientId = sessionClientId ?? getLastClientId();
      if (lastClientId) {
        dispatch(fetchClientById(lastClientId)).then((result) => {
          const encryptedId = encryptDecrypt.encryptClientId(lastClientId);
          dispatch(updateMenu(encryptedId));
          const reEngage = result.payload?.reEngage === true;
          dispatch(setAllowReEngage(reEngage));
        });
      } else {
        dispatch(updateMenu(null));
      }
    }
    const currentAgent = getActiveAgent();
    if (currentAgent) {
      dispatch(setCurrentAgent(currentAgent));
    }
  }, []);

  const logout = async () => {
    try {
      removeAgentIframe();

      await AxiosInstance.post('/auth/logout');

      await AxiosInstance.post('/sessionLog/create', {
        client: clientName as string,
        clientTeam: user?.data.user._id,
        sessionStart: pageDetails[0]?.time,
        sessionEnd: new Date(),
        pageDetails,
      });
    } catch (error) {
      console.log(error);
    }

    localStorage.clear();
    localStorage.setItem('force_logout_v2', 'true');

    try {
      clearLastClientId();
      dispatch(resetStore());
      dispatch(clearActiveClient());
      dispatch(resetPages());
      dispatch(setOrganizationId('all'));
      dispatch(setBranchId('all'));
    } catch (error) {
      console.log(error);
    }

    navigate('/login', { replace: true });
  };

  const toggleCompactMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    setCompactMenu(!compactMenu);
  };

  useEffect(() => {
    const getUser = async () => {
      try {
        const response = await AxiosInstance.get('/client-teams');
        const reEngage = response.data.data.reEngage || false;

        if ([ROLE.OWNER, ROLE.ADMIN].includes(response.data?.data?.user?.role)) {
          if (!activeClient.id) {
            dispatch(
              updateActiveClientMeta({
                salesReport: response.data?.data?.salesReport ?? false,
                reEngage: reEngage,
              }),
            );
            dispatch(setAllowReEngage(reEngage));
          }
        }
      } catch (error) {
        if (!import.meta.env.DEV) {
          logout();
        }
      }
    };
    getUser();
  }, []);

  useEffect(() => {
    const fetchHubspotIdsAndAgents = async () => {
      try {
        const res = await AxiosInstance.get(`/clients/${client}`);
        console.log('Fetched client hubspot ids:', res.data.data?.hubspotCompanyIds);
        dispatch(setHubspotCompanyIds(res.data.data?.hubspotCompanyIds ?? []));
        dispatch(setAgents(res.data.data?.agents ?? null));
        const showGoogleAnalytics = res.data.data?.googleAnalytics.propertyId ? true : false;
        dispatch(setGoogleAnalyticBool(showGoogleAnalytics));
        dispatch(setGoogleAnalytics(showGoogleAnalytics));
      } catch (err) {
        console.error('Failed to fetch HubSpot company IDs', err);
      }
    };
    fetchHubspotIdsAndAgents();
  }, [client]);

  const compactLayout = () => {
    if (window.innerWidth <= 1600) {
      setCompactMenu(true);
    }
  };

  const requestFullscreen = () => {
    const el = document.documentElement;
    if (el.requestFullscreen) {
      el.requestFullscreen();
    }
  };

  const { user } = useAppSelector((state: RootState) => state.users);

  const chatUsername = user?.data?.user?.name || '';
  const chatIsSuperowner = isSuperOwner;
  const chatListRefreshKey = chatSidebar.chatListRefreshKey;

  useEffect(() => {
    if (scrollableRef.current) {
      new SimpleBar(scrollableRef.current);
    }

    setFormattedMenu(sideMenu());

    // Only the first section starts open. Sections the user has toggled keep
    // their choice; the rest follow the default as the menu changes shape.
    const defaults = collapsedByDefault(
      filterMenuForSettingsView(sideMenuStore, settingsView)
    );
    setCollapsedSections((prev) => {
      const next: Record<string, boolean> = { ...defaults };
      for (const key of Object.keys(prev)) {
        if (touchedSections[key]) next[key] = prev[key];
      }
      return next;
    });

  }, [sideMenuStore, location, settingsView, touchedSections]);

  // Deliberately separate from the effect above. compactLayout() collapses the
  // whole sidebar below 1600px, and while it sat in that effect every section
  // toggle re-ran it — expanding one section shrank the entire nav.
  useEffect(() => {
    compactLayout();
    window.addEventListener('resize', compactLayout);
    return () => window.removeEventListener('resize', compactLayout);
  }, []);

  const [breadcrumbValue, setBreadCrumbValue] = useState<PathType>({
    path: '/',
    name: '',
  });

  useEffect(() => {
    if (!user) {
      dispatch(getClientTeam());
    }
  }, []);

  useEffect(() => {
    setImgUrl(user?.data?.user?.media?.path as string);
  }, [user]);

  useEffect(() => {
    setBreadCrumbValue({
      path: location.pathname,
      name: deriveBreadcrumbName(location.pathname),
    });
  }, [location]);

  return (
    <div
      className={clsx([
        'echo group  background relative flex flex-col min-h-screen bg-slate-50',
        !isChatPage && 'bg-gradient-to-b from-slate-200/70 to-slate-50',
      ])}
    >
      <div className='flex fixed z-50 h-full'>
        <AgentRail mobileOpen={activeMobileMenu} />

        <div
          className={clsx([
            'xl:ml-0 shadow-xl transition-[margin,padding] duration-300 xl:shadow-none side-menu group inset-y-0',
            "after:content-[''] after:fixed after:inset-0 after:bg-black/80 after:xl:hidden",
            { 'side-menu--collapsed': compactMenu },
            { 'ml-0 after:block': activeMobileMenu },
            { '-ml-[275px] after:hidden': !activeMobileMenu },
          ])}
        >
          <div
            className={clsx([
              !isBeaconPage && 'ml-[280px]',
              'fixed w-full h-full items-center justify-center xl:hidden z-50',
              { flex: activeMobileMenu },
              { hidden: !activeMobileMenu },
            ])}
          >
            <a
              href=''
              onClick={(event) => {
                event.preventDefault();
                setActiveMobileMenu(false);
              }}
              className='relative mt-3 ml-5 h-[95vh] w-full'
            >
              <Lucide icon='X' className=' w-8 h-8 text-white' />
            </a>
          </div>

          {!isBeaconPage && (
            <div
              className={clsx([
                'h-full box bg-white/[0.95] rounded-none z-20 relative w-[275px] duration-300 transition-[width] group-[.side-menu--collapsed]:xl:w-[91px] overflow-hidden flex flex-col',
              ])}
            >
              <div
                className={clsx([
                  'flex-none hidden xl:flex items-center justify-center z-10 px-3 h-[65px] w-[275px] overflow-hidden relative duration-300 group-[.side-menu--collapsed]:xl:w-[91px]',
                ])}
              >
                {isSuperOwner ? (
                  <div className='group-[.side-menu--collapsed]:xl:hidden'>
                    <SidebarDropdownClient />
                  </div>
                ) : (
                  <div className='ml-3.5 group-[.side-menu--collapsed]:xl:hidden transition-opacity font-medium whitespace-nowrap'>
                    KEEPME
                  </div>
                )}
                <a
                  href=''
                  onClick={toggleCompactMenu}
                  aria-label={compactMenu ? 'Expand sidebar' : 'Collapse sidebar'}
                  title={compactMenu ? 'Expand sidebar' : 'Collapse sidebar'}
                  className={clsx([
                    'hidden xl:flex group-[.side-menu--collapsed]:xl:rotate-180 transition-transform items-center justify-center w-[20px] h-[20px] shrink-0 border rounded-full border-slate-600/40 hover:bg-slate-600/5',
                    compactMenu ? 'mx-auto' : 'ml-auto',
                  ])}
                >
                  <Lucide icon='ArrowLeft' className='w-3.5 h-3.5 stroke-[1.3]' />
                </a>
              </div>

              {isSuperOwner && (
                <div className='block xl:hidden mt-4 ml-4'>
                  <SidebarDropdownClient />
                </div>
              )}

              {showChatSidebar ? (
                <ChatSidebar
                  username={chatUsername}
                  isSuperowner={chatIsSuperowner}
                  canDeleteChats={true}
                  activeChatId={routeChatId ?? ''}
                  refreshKey={chatListRefreshKey}
                  compactMenu={compactMenu}
                  fetchLlm={chatFetchLlm}
                  onNewChat={handleChatNewChat}
                  onSelectChat={handleChatSelect}
                  onAuthFailure={handleChatAuthFailure}
                />
              ) : (
                <div
                  ref={scrollableRef}
                  className={clsx([
                    'w-full h-full z-20 px-5 overflow-y-auto overflow-x-hidden pb-3 [-webkit-mask-image:-webkit-linear-gradient(top,rgba(0,0,0,0),black_30px)] [&:-webkit-scrollbar]:w-0 [&:-webkit-scrollbar]:bg-transparent',
                    '[&_.simplebar-content]:p-0 [&_.simplebar-track.simplebar-vertical]:w-[10px] [&_.simplebar-track.simplebar-vertical]:mr-0.5 [&_.simplebar-track.simplebar-vertical_.simplebar-scrollbar]:before:bg-slate-400/30',
                  ])}
                >
                  <ul className='scrollable'>
                    {(() => {
                      let sectionCollapsed = false;
                      return formattedMenu.map((menu, menuKey) => {
                        if (typeof menu === 'string') {
                          if (menu.trim() === '') {
                            sectionCollapsed = false;
                            return (
                              <li className='side-menu__separator' key={menuKey}>
                                <hr className='my-3 border-slate-300/50 group-[.side-menu--collapsed]:xl:mx-2' />
                              </li>
                            );
                          }

                          const isAccordion = ACCORDION_SECTIONS.has(menu);
                          const isCollapsed = isAccordion && !!collapsedSections[menu];
                          sectionCollapsed = isCollapsed;

                          return (
                            <li
                              className={clsx([
                                'side-menu__divider',
                                { 'side-menu__divider--accordion': isAccordion },
                              ])}
                              key={menuKey}
                            >
                              {isAccordion ? (
                                <a
                                  href=''
                                  className='flex items-center justify-between group-[.side-menu--collapsed]:xl:justify-center text-slate-900'
                                  onClick={(event: React.MouseEvent) => {
                                    event.preventDefault();
                                    toggleSection(menu);
                                  }}
                                >
                                  <span className='group-[.side-menu--collapsed]:xl:hidden'>
                                    {menu}
                                  </span>
                                  <Lucide
                                    icon='ChevronDown'
                                    className={clsx([
                                      'w-3.5 h-3.5 transition-transform duration-200 font-semibold text-primary stroke-2 stroke-primary',
                                      { '-rotate-90': isCollapsed },
                                    ])}
                                  />
                                </a>
                              ) : (
                                menu
                              )}
                            </li>
                          );
                        }

                        if (sectionCollapsed) {
                          return null;
                        }

                        return (
                          <li key={menuKey}>
                            {compactMenu ? (
                              <Tippy
                                as='a'
                                href=''
                                content={menu.title}
                                className={clsx([
                                  'side-menu__link',
                                  { 'side-menu__link--active': menu.active },
                                  { 'side-menu__link--active-dropdown': menu.activeDropdown },
                                ])}
                                onClick={(event: React.MouseEvent) => {
                                  event.preventDefault();
                                  linkTo(menu, navigate, undefined, () => setOpenReleaseNotes(true));
                                  setFormattedMenu([...formattedMenu]);
                                  setActiveMobileMenu(false);
                                }}
                              >
                                <Lucide icon={menu.icon} className='side-menu__link__icon' />
                                <div className='side-menu__link__title'>{menu.title}</div>
                                {menu.badge && (
                                  <div className='side-menu__link__badge'>{menu.badge}</div>
                                )}
                                {menu.subMenu && (
                                  <Lucide icon='ChevronDown' className='side-menu__link__chevron' />
                                )}
                              </Tippy>
                            ) : (
                              <a
                                href=''
                                className={clsx([
                                  'side-menu__link',
                                  { 'side-menu__link--active': menu.active },
                                  { 'side-menu__link--active-dropdown': menu.activeDropdown },
                                ])}
                                onClick={(event: React.MouseEvent) => {
                                  event.preventDefault();
                                  linkTo(menu, navigate, undefined, () => setOpenReleaseNotes(true));
                                  setFormattedMenu([...formattedMenu]);
                                  setActiveMobileMenu(false);
                                }}
                              >
                                <Lucide icon={menu.icon} className='side-menu__link__icon' />
                                <div className='side-menu__link__title'>{menu.title}</div>
                                {menu.badge && (
                                  <div className='side-menu__link__badge'>{menu.badge}</div>
                                )}
                                {menu.subMenu && (
                                  <Lucide icon='ChevronDown' className='side-menu__link__chevron' />
                                )}
                              </a>
                            )}
                            {menu.subMenu && (
                              <Transition
                                in={menu.activeDropdown}
                                onEnter={enter}
                                onExit={leave}
                                timeout={300}
                              >
                                <ul
                                  className={clsx([
                                    '',
                                    { block: menu.activeDropdown },
                                    { hidden: !menu.activeDropdown },
                                  ])}
                                >
                                  {menu.subMenu.map((subMenu, subMenuKey) => (
                                    <li key={subMenuKey}>
                                      {compactMenu ? (
                                        <Tippy
                                          as='a'
                                          href=''
                                          content={subMenu.title}
                                          className={clsx([
                                            'side-menu__link',
                                            { 'side-menu__link--active': subMenu.active },
                                            {
                                              'side-menu__link--active-dropdown':
                                                subMenu.activeDropdown,
                                            },
                                          ])}
                                          onClick={(event: React.MouseEvent) => {
                                            event.preventDefault();
                                            linkTo(subMenu, navigate, undefined, () =>
                                              setOpenReleaseNotes(true),
                                            );
                                            setFormattedMenu([...formattedMenu]);
                                          }}
                                        >
                                          <Lucide
                                            icon={subMenu.icon}
                                            className='side-menu__link__icon'
                                          />
                                          <div className='side-menu__link__title'>
                                            {subMenu.title}
                                          </div>
                                          {subMenu.badge && (
                                            <div className='side-menu__link__badge'>
                                              {subMenu.badge}
                                            </div>
                                          )}
                                          {subMenu.subMenu && (
                                            <Lucide
                                              icon='ChevronDown'
                                              className='side-menu__link__chevron'
                                            />
                                          )}
                                        </Tippy>
                                      ) : (
                                        <a
                                          href=''
                                          className={clsx([
                                            'side-menu__link',
                                            { 'side-menu__link--active': subMenu.active },
                                            {
                                              'side-menu__link--active-dropdown':
                                                subMenu.activeDropdown,
                                            },
                                          ])}
                                          onClick={(event: React.MouseEvent) => {
                                            event.preventDefault();
                                            linkTo(subMenu, navigate, undefined, () =>
                                              setOpenReleaseNotes(true),
                                            );
                                            setFormattedMenu([...formattedMenu]);
                                          }}
                                        >
                                          <Lucide
                                            icon={subMenu.icon}
                                            className='side-menu__link__icon'
                                          />
                                          <div className='side-menu__link__title'>
                                            {subMenu.title}
                                          </div>
                                          {subMenu.badge && (
                                            <div className='side-menu__link__badge'>
                                              {subMenu.badge}
                                            </div>
                                          )}
                                          {subMenu.subMenu && (
                                            <Lucide
                                              icon='ChevronDown'
                                              className='side-menu__link__chevron'
                                            />
                                          )}
                                        </a>
                                      )}
                                      {subMenu.subMenu && (
                                        <Transition
                                          in={subMenu.activeDropdown}
                                          onEnter={enter}
                                          onExit={leave}
                                          timeout={300}
                                        >
                                          <ul
                                            className={clsx([
                                              '',
                                              { block: subMenu.activeDropdown },
                                              { hidden: !subMenu.activeDropdown },
                                            ])}
                                          >
                                            {subMenu.subMenu.map((lastSubMenu, lastSubMenuKey) => (
                                              <li key={lastSubMenuKey}>
                                                {compactMenu ? (
                                                  <Tippy
                                                    as='a'
                                                    href=''
                                                    content={lastSubMenu.title}
                                                    className={clsx([
                                                      'side-menu__link',
                                                      {
                                                        'side-menu__link--active':
                                                          lastSubMenu.active,
                                                      },
                                                      {
                                                        'side-menu__link--active-dropdown':
                                                          lastSubMenu.activeDropdown,
                                                      },
                                                    ])}
                                                    onClick={(event: React.MouseEvent) => {
                                                      event.preventDefault();
                                                      linkTo(lastSubMenu, navigate, undefined, () =>
                                                        setOpenReleaseNotes(true),
                                                      );
                                                      setFormattedMenu([...formattedMenu]);
                                                    }}
                                                  >
                                                    <Lucide
                                                      icon={lastSubMenu.icon}
                                                      className='side-menu__link__icon'
                                                    />
                                                    <div className='side-menu__link__title'>
                                                      {lastSubMenu.title}
                                                    </div>
                                                    {lastSubMenu.badge && (
                                                      <div className='side-menu__link__badge'>
                                                        {lastSubMenu.badge}
                                                      </div>
                                                    )}
                                                  </Tippy>
                                                ) : (
                                                  <a
                                                    href=''
                                                    className={clsx([
                                                      'side-menu__link',
                                                      {
                                                        'side-menu__link--active':
                                                          lastSubMenu.active,
                                                      },
                                                      {
                                                        'side-menu__link--active-dropdown':
                                                          lastSubMenu.activeDropdown,
                                                      },
                                                    ])}
                                                    onClick={(event: React.MouseEvent) => {
                                                      event.preventDefault();
                                                      linkTo(lastSubMenu, navigate, undefined, () =>
                                                        setOpenReleaseNotes(true),
                                                      );
                                                      setFormattedMenu([...formattedMenu]);
                                                    }}
                                                  >
                                                    <Lucide
                                                      icon={lastSubMenu.icon}
                                                      className='side-menu__link__icon'
                                                    />
                                                    <div className='side-menu__link__title'>
                                                      {lastSubMenu.title}
                                                    </div>
                                                    {lastSubMenu.badge && (
                                                      <div className='side-menu__link__badge'>
                                                        {lastSubMenu.badge}
                                                      </div>
                                                    )}
                                                  </a>
                                                )}
                                              </li>
                                            ))}
                                          </ul>
                                        </Transition>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              </Transition>
                            )}
                          </li>
                        );
                      });
                    })()}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className='fixed h-[65px] transition-[margin] duration-100 xl:ml-[343px] group-[.side-menu--collapsed]:xl:ml-[158px] mt-3.5 inset-x-0 top-0'>
            <div
              className={clsx([
                'top-bar absolute left-0 xl:left-3.5 right-0 h-full mx-5 group',
                "before:content-[''] before:absolute before:top-0 before:inset-x-0 before:-mt-[15px] before:h-[20px] before:backdrop-blur",
                'top-bar--active',
              ])}
            >
              <div
                className={clsx([
                  'container flex items-center w-full h-full transition-[padding,background-color,border-color] ease-in-out duration-300 box bg-transparent border-transparent shadow-none',
                  !isChatPage
                    ? 'group-[.top-bar--active]:box group-[.top-bar--active]:px-5  group-[.top-bar--active]:bg-transparent group-[.top-bar--active]:border-transparent group-[.top-bar--active]:bg-gradient-to-r group-[.top-bar--active]:from-theme-1 group-[.top-bar--active]:to-theme-2'
                    : 'box px-5 bg-gradient-to-r from-theme-1 to-theme-2',
                ])}
              >
                <div className='flex items-center gap-1 xl:hidden'>
                  <a
                    href=''
                    onClick={(event) => {
                      event.preventDefault();
                      setActiveMobileMenu(true);
                    }}
                    className='p-2 text-white rounded-full hover:bg-white/5'
                  >
                    <Lucide icon='AlignJustify' className='w-[18px] h-[18px]' />
                  </a>
                </div>
                {(() => {
                  const beaconTrail = deriveBeaconTrail(location.pathname);
                  const rootHref =
                    location.pathname === '/metrics'
                      ? '/metrics'
                      : location.pathname === '/accounting'
                        ? '/accounting'
                        : '/';
                  const rootLabel = location.pathname === '/clients' ? 'Home' : clientName;

                  const links = beaconTrail
                    ? [
                      <Breadcrumb.Link to={rootHref}>{rootLabel}</Breadcrumb.Link>,
                      ...beaconTrail.map((seg, i) => (
                        <Breadcrumb.Link to={seg.path} active={i === beaconTrail.length - 1}>
                          {seg.label}
                        </Breadcrumb.Link>
                      )),
                    ]
                    : isChatPage
                      ? [
                        <Breadcrumb.Link to={rootHref} active={true}>
                          {rootLabel}
                        </Breadcrumb.Link>,
                      ]
                      : [
                        <Breadcrumb.Link to={rootHref}>{rootLabel}</Breadcrumb.Link>,
                        <Breadcrumb.Link
                          to={breadcrumbValue.path}
                          active={true}
                          className='capitalize'
                        >
                          {camelToTitleCase(breadcrumbValue.name)}
                        </Breadcrumb.Link>,
                      ];
                  return (
                    <Breadcrumb light className='flex-1 hidden xl:block'>
                      {links}
                    </Breadcrumb>
                  );
                })()}
                <div className='relative justify-center flex-1 flex'>
                  <img alt={appImages.logoLight.alt} src={appImages.logoLight.src} width='140' />
                </div>

                <div className='flex items-center flex-1'>
                  <div className='flex items-center gap-1 ml-auto'>
                    <a
                      href=''
                      className='p-2 text-white rounded-full hover:bg-white/5'
                      onClick={(e) => {
                        e.preventDefault();
                        requestFullscreen();
                      }}
                    >
                      <Lucide icon='Expand' className='w-[18px] h-[18px]' />
                    </a>
                    <a
                      href=''
                      title='Contact Support'
                      className='p-2 text-white rounded-full hover:bg-white/5'
                      onClick={(e) => {
                        e.preventDefault();
                        setOpenSupport(true);
                      }}
                    >
                      <Lucide icon='Headphones' className='w-[18px] h-[18px]' />
                    </a>
                    <SupportSelectionModal
                      hideIcon
                      externalOpen={openSupport}
                      onExternalClose={() => setOpenSupport(false)}
                    />
                  </div>
                  <ReleaseNotesModal
                    externalOpen={openReleaseNotes}
                    onExternalClose={() => setOpenReleaseNotes(false)}
                  />
                  <Menu className='ml-5'>
                    <Menu.Button className='overflow-hidden rounded-full w-[36px] h-[36px] border-[3px] border-white/[0.15] image-contain bg-white'>
                      <img src={clientProfilePic ?? userFallback} />
                    </Menu.Button>
                    <Menu.Items className='w-64 mt-1 px-4'>
                      <div className='p-3 text-base font-semibold'>{user?.data?.user.name}</div>

                      <ul className='relative'>
                        <li>
                          {(() => {
                            const isProfileActive =
                              location.pathname.replace(/\/$/, '').split('/').pop() === 'profile';
                            return (
                              <Menu.Item
                                as='a'
                                onClick={(event: React.MouseEvent) => {
                                  event.preventDefault();
                                  navigate('profile');
                                }}
                                className={clsx([
                                  'flex items-center rounded-[0.6rem] border border-transparent mb-2 text-sm',
                                  'transition-all duration-200 ease-in',
                                  isProfileActive
                                    ? 'bg-white border-slate-200 shadow-[0px_2px_3px_#0000000b] dark:bg-darkmode-400 dark:border-darkmode-300'
                                    : 'text-slate-600 hover:bg-white/80 hover:border-slate-200/80 hover:shadow-[0px_2px_3px_#0000000b] dark:text-slate-300 dark:hover:bg-darkmode-400/80',
                                ])}
                              >
                                <Lucide icon='Users' className='w-4 h-4 mr-3 flex-none' />
                                <div
                                  className={clsx([
                                    'overflow-hidden text-ellipsis whitespace-nowrap',
                                    isProfileActive
                                      ? 'text-theme-1 font-medium'
                                      : 'text-slate-600 dark:text-slate-300',
                                  ])}
                                >
                                  Profile Info
                                </div>
                              </Menu.Item>
                            );
                          })()}
                        </li>
                      </ul>

                      <Menu.Item onClick={logout}>
                        <Lucide icon='Power' className='w-4 h-4 mr-4' />
                        Logout
                      </Menu.Item>
                    </Menu.Items>
                  </Menu>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className={clsx([
          'transition-[margin,width] duration-100 xl:pl-3.5 pt-[54px] relative z-10 group mode flex-1 flex flex-col',
          { 'pb-16': !isChatPage },
          { 'xl:ml-[343px]': !compactMenu },
          { 'xl:ml-[159px]': compactMenu },
        ])}
      >
        <div className='px-5 mt-16 flex-1 flex flex-col'>
          <div className='container h-full flex-1 flex flex-col'>
            <Suspense fallback={<Loading />}>
              <Outlet />
            </Suspense>
          </div>
        </div>
      </div>
      <UiFeedbackPrompt />
    </div>
  );
}

export default Main;